<template>
  <ManagerLayout>
    <div class="manager-line-funnel">
      <h2 class="page-title">LINEファネル進捗</h2>
      <p class="page-desc">
        LINE名を起点に、友だち追加からDay0体験・7日間登録・Day1/Day7提出まで、
        今どこまで進んでいるかを1人1行で表示します。
      </p>

      <section class="manager-section filters-section">
        <div class="card">
          <div class="filters">
            <label class="filter-item filter-search">
              <span class="filter-label">検索</span>
              <input
                v-model="searchQ"
                type="text"
                placeholder="LINE名で部分一致"
                class="form-input"
                @keyup.enter="loadUsers"
              />
            </label>
            <label class="filter-item">
              <span class="filter-label">期間</span>
              <select v-model="days" class="form-input" @change="loadUsers">
                <option value="7">直近7日</option>
                <option value="30">直近30日</option>
                <option value="90">直近90日</option>
                <option value="180">直近180日</option>
              </select>
            </label>
            <button type="button" class="btn btn-primary" :disabled="loading" @click="loadUsers">
              {{ loading ? '読み込み中...' : '検索' }}
            </button>
          </div>
        </div>
      </section>

      <section class="manager-section">
        <div class="card">
          <div class="toolbar">
            <span class="total-note">全 {{ users.length }} 件（最終更新日時が新しい順）</span>
            <button type="button" class="btn btn-secondary btn-sm" :disabled="loading" @click="loadUsers">
              更新
            </button>
          </div>
          <div v-if="loading" class="loading-state">読み込み中...</div>
          <div v-else-if="!users.length" class="empty-state">該当するユーザーがいません</div>
          <div v-else class="table-wrap">
            <table class="data-table" aria-label="LINEファネル進捗">
              <thead>
                <tr>
                  <th class="sticky-col">LINE名</th>
                  <th>対象</th>
                  <th
                    v-for="cp in checkpoints"
                    :key="cp.key"
                    class="center checkpoint-header"
                    :title="cp.label.replace('\n', '')"
                  >{{ cp.label }}</th>
                  <th>最終更新（JST）</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="u in users" :key="u.lineUserId" class="data-row">
                  <td class="sticky-col">{{ u.lineDisplayName || '（未取得）' }}</td>
                  <td>{{ audienceLabel(u.audience) }}</td>
                  <td
                    v-for="cp in checkpoints"
                    :key="cp.key"
                    class="center"
                    :title="u.checkpoints[cp.key] ? formatDateTimeJst(u.checkpoints[cp.key]) : '未到達'"
                  >
                    <span v-if="u.checkpoints[cp.key]" class="check-mark">✅</span>
                    <span v-else class="check-empty">—</span>
                  </td>
                  <td>{{ formatDateTimeJst(u.latestAt) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  </ManagerLayout>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import ManagerLayout from '../../components/layout/ManagerLayout.vue'
import { supabase } from '../../supabaseClient'

const apiBase = import.meta.env.VITE_API_BASE_URL || ''

const searchQ = ref('')
const days = ref('30')
const users = ref([])
const checkpoints = ref([])
const loading = ref(false)

const getToken = async () => {
  const { data: { session } } = await supabase.auth.getSession()
  return session?.access_token
}

function formatDateTimeJst (iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo', dateStyle: 'short', timeStyle: 'short' })
}

function audienceLabel (audience) {
  if (audience === 'child') return 'お子さま'
  if (audience === 'adult') return '大人'
  return '—'
}

const loadUsers = async () => {
  loading.value = true
  try {
    const token = await getToken()
    const params = new URLSearchParams()
    if (searchQ.value.trim()) params.set('q', searchQ.value.trim())
    params.set('days', days.value)
    const res = await fetch(`${apiBase}/api/manager/line-funnel?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      users.value = []
      return
    }
    users.value = data.users || []
    checkpoints.value = data.checkpoints || []
  } catch (_) {
    users.value = []
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  loadUsers()
})
</script>

<style scoped>
.manager-line-funnel {
  padding: 0;
}

.page-title {
  margin: 0 0 0.5rem 0;
  font-size: 1.5rem;
  color: #1e293b;
}

.page-desc {
  margin: 0 0 1.5rem 0;
  color: #64748b;
  font-size: 0.9rem;
}

.manager-section {
  margin-bottom: 2rem;
}

.card {
  background: white;
  border-radius: 8px;
  padding: 1.25rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1rem;
}

.filter-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.filter-label {
  font-size: 0.85rem;
  color: #64748b;
  font-weight: 500;
}

.filter-search .form-input {
  min-width: 220px;
}

.form-input {
  padding: 0.4rem 0.6rem;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font-size: 0.9rem;
}

.toolbar {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
}

.total-note {
  font-size: 0.9rem;
  color: #64748b;
}

.btn {
  padding: 0.4rem 0.9rem;
  border-radius: 6px;
  font-size: 0.9rem;
  cursor: pointer;
  border: 1px solid transparent;
}

.btn-primary {
  background: #3b82f6;
  color: white;
  border-color: #3b82f6;
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  background: #e2e8f0;
  color: #334155;
  border-color: #cbd5e1;
}

.btn-sm {
  padding: 0.25rem 0.5rem;
  font-size: 0.85rem;
}

.loading-state,
.empty-state {
  padding: 2rem;
  text-align: center;
  color: #64748b;
}

.table-wrap {
  overflow-x: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}

.data-table th,
.data-table td {
  padding: 0.5rem 0.75rem;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
}

.data-table th {
  background: #f8fafc;
  color: #475569;
  font-weight: 600;
}

.data-table th.center,
.data-table td.center {
  text-align: center;
}

.checkpoint-header {
  white-space: pre-line;
  line-height: 1.3;
  font-size: 0.78rem;
}

.sticky-col {
  position: sticky;
  left: 0;
  background: white;
  z-index: 1;
}

.data-table th.sticky-col {
  background: #f8fafc;
  z-index: 2;
}

.check-mark {
  color: #16a34a;
}

.check-empty {
  color: #cbd5e1;
}
</style>
